import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { isTokenExpiradoComTolerancia } from '../../utils/jwt';
import { performLogout } from '../../utils/authLogout';
import FullScreenLoader from '../loading/FullScreenLoader';

interface PrivateRouteProps {
  element: React.ReactElement;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ element }) => {
  const { token, isRefreshing, sessaoIndisponivel } = useAuth();
  const expirado = !!token && isTokenExpiradoComTolerancia(token);

  // Token presente e expirado sem renovação em andamento: caminho de saída que
  // antes era um `<Navigate to="/login" />` seco — sem limpar o store e sem
  // dizer nada ao usuário (mais um logout silencioso). Agora passa pelo
  // `performLogout`, que limpa o estado e deixa o aviso para a tela de login.
  // Em efeito (não no render) porque `performLogout` dispara ações e redirect.
  //
  // Na prática este é um caminho DEFENSIVO: o gate de bootstrap do `App` já
  // segura o render antes das rotas quando o token está expirado. O
  // `performLogout` é idempotente (guarda `loggingOut`), então não há risco de
  // logout duplicado se os dois caminhos se cruzarem.
  // ⚠️ `sessaoIndisponivel` é condição de parada obrigatória: token expirado com
  // o servidor inalcançável NÃO é sessão encerrada. Sem essa guarda, o fim da
  // tentativa (`isRefreshing: false`) deslogaria exatamente o usuário offline
  // que a at-22 existe para preservar. Quem cuida da tela nesse caso é o gate do
  // `App` (aviso + nova tentativa), que reage ao mesmo flag.
  useEffect(() => {
    if (expirado && !isRefreshing && !sessaoIndisponivel) {
      void performLogout('expirada');
    }
  }, [expirado, isRefreshing, sessaoIndisponivel]);

  // Sem token → não autenticado (nada expirou: não há aviso a dar).
  if (!token) {
    return <Navigate to="/login" />;
  }

  // Expirado: há renovação em andamento (proativa OU reativa, desde a at-22) ou
  // o logout acima está em curso. Nos dois casos aguardar, nunca "pular" para o
  // login por conta própria.
  //
  // A tela fica MONTADA atrás do loader (que é um overlay `position: fixed`) de
  // propósito: trocar `element` pelo loader desmontava a página e jogava fora o
  // estado local do formulário em andamento — e agora que o refresh reativo
  // também acende o `isRefreshing`, isso aconteceria no meio de uma adesão a
  // cada renovação. O overlay bloqueia a interação sem destruir o preenchimento.
  if (expirado) {
    return (
      <>
        {element}
        <FullScreenLoader />
      </>
    );
  }

  return element;
};

export default PrivateRoute;
